import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  Skeleton,
  Button,
  Pagination,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import VisibilityIcon from "@mui/icons-material/Visibility";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import IconButton from "@mui/material/IconButton";
import { api } from "../../services/api";
import ImageWithFallback from "../../components/ImageWithFallback";
import { sortBoardMembersByRank } from "../../section/board/constants";

// 5 members per row on desktop; card effect (shadow, hover)
const MembersGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(5, 1fr)",
  gap: "30px",
  marginBottom: "40px",
  [theme.breakpoints.down("lg")]: {
    gridTemplateColumns: "repeat(3, 1fr)",
  },
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));

const MemberCard = styled(Box)(({ theme }) => ({
  textAlign: "center",
  padding: "24px 16px",
  borderRadius: "12px",
  backgroundColor: "#fff",
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  "& img": {
    width: "180px",
    height: "180px",
    borderRadius: "50%",
    marginBottom: "15px",
    objectFit: "cover",
  },
  "& h3": {
    fontSize: "20px",
    color: "#333",
    margin: "0 0 5px 0",
  },
  "& p": {
    fontSize: "16px",
    color: "#666",
    margin: 0,
    fontStyle: "italic",
  },
}));

// Format as "15 February, 2025"
const formatBoardDate = (date) => {
  if (!date) return "—";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "—";
  const day = d.getDate();
  const month = d.toLocaleDateString("en-US", { month: "long" });
  const year = d.getFullYear();
  return `${day} ${month}, ${year}`;
};

// Start date from API fields or from MongoDB ObjectId (first 8 hex chars = creation timestamp)
const getStartDate = (member) => {
  if (!member) return null;
  if (member.effectiveStartDate || member.startDate || member.createdAt) {
    return member.effectiveStartDate || member.startDate || member.createdAt;
  }
  const id = member._id;
  if (!id) return null;
  const hex = typeof id === "string" ? id : (id.toString && id.toString());
  if (!hex || hex.length < 8) return null;
  const sec = parseInt(hex.slice(0, 8), 16);
  if (Number.isNaN(sec)) return null;
  return new Date(sec * 1000).toISOString();
};

const ARCHIVE_PAGE_SIZE = 20;

const BoardMembersList = () => {
  const [current, setCurrent] = useState([]);
  const [archived, setArchived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [archiveExpanded, setArchiveExpanded] = useState(false);
  const [archivePage, setArchivePage] = useState(1);

  const archivePageCount = Math.ceil(archived.length / ARCHIVE_PAGE_SIZE) || 1;
  const archivedPageSlice = archived.slice(
    (archivePage - 1) * ARCHIVE_PAGE_SIZE,
    archivePage * ARCHIVE_PAGE_SIZE
  );

  useEffect(() => {
    // Single endpoint: GET /board-members (same as admin). Split current vs archived client-side so it works even if archived/list is not deployed.
    const load = async () => {
      setLoading(true);
      try {
        const res = await api.get("/board-members");
        const all =
          res?.data?.data ??
          (Array.isArray(res?.data) ? res.data : []);
        const allList = Array.isArray(all) ? all : [];
        const currentList = allList.filter(
          (m) => m.endDate == null || m.endDate === undefined
        );
        const archivedList = allList.filter(
          (m) => m.endDate != null && m.endDate !== undefined
        );
        setCurrent(sortBoardMembersByRank(currentList));
        setArchived(sortBoardMembersByRank(archivedList));
      } catch (err) {
        console.error("Board members load error:", err);
        setCurrent([]);
        setArchived([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <Box sx={{ pt: 3, pb: 6 }}>
        <Container maxWidth="xl" disableGutters sx={{ px: '5%' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "rgba(26,128,200)", textAlign: "center", mb: 1, mt: 3 }}>
            Board Members
          </Typography>
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, mb: 3 }} />
          <Grid container spacing={3}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 3, pb: 6, minHeight: "60vh", backgroundColor: "#f8f8f8" }}>
      <Container maxWidth="xl" disableGutters sx={{ px: '5%' }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: "rgba(26,128,200)", textAlign: "center", mb: 0.5, mt: 3 }}>
          Board Members
        </Typography>
        <Typography sx={{ textAlign: "center", mb: 4, color: "#666", fontSize: "1rem" }}>
          Current and past leadership of Hamsa Lomi Ethiopian Association
        </Typography>

        <Typography variant="h6" sx={{ mb: 2, color: "#333", fontWeight: 600 }}>
          Current Members
        </Typography>
        {current.length === 0 ? (
          <Typography sx={{ py: 2, color: "#666" }}>No current board members listed.</Typography>
        ) : (
          <MembersGrid sx={{ mb: 5 }}>
            {current.map((member) => (
              <MemberCard key={member._id}>
                <ImageWithFallback
                  path={member.image}
                  alt={member.name}
                  style={{ objectFit: "contain", backgroundColor: "white" }}
                />
                <Typography variant="h3">{member.name}</Typography>
                <Typography variant="body1">{member.title}</Typography>
                <Typography variant="body2" color="#666" sx={{ mt: 0.5 }}>
                  {formatBoardDate(getStartDate(member))} – Current
                </Typography>
                <Button
                  component={Link}
                  to={`/board-members/${member._id}`}
                  variant="outlined"
                  size="small"
                  startIcon={<VisibilityIcon />}
                  sx={{ mt: 2 }}
                >
                  View
                </Button>
              </MemberCard>
            ))}
          </MembersGrid>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mb: 2,
            cursor: "pointer",
            "&:hover": { opacity: 0.85 },
          }}
          onClick={() => {
            setArchiveExpanded((prev) => !prev);
            if (!archiveExpanded) setArchivePage(1);
          }}
          role="button"
          aria-expanded={archiveExpanded}
        >
          <Typography variant="h6" sx={{ color: "#333", fontWeight: 600 }}>
            Past Members (Archive)
          </Typography>
          <IconButton
            size="small"
            aria-label={archiveExpanded ? "Collapse archive" : "Expand archive"}
            sx={{ p: 0.25 }}
            onClick={(e) => {
              e.stopPropagation();
              setArchiveExpanded((prev) => !prev);
              if (archiveExpanded) setArchivePage(1);
            }}
          >
            {archiveExpanded ? (
              <KeyboardArrowUpIcon sx={{ fontSize: 28 }} />
            ) : (
              <KeyboardArrowDownIcon sx={{ fontSize: 28 }} />
            )}
          </IconButton>
        </Box>
        {archiveExpanded && (
          <>
            {archived.length === 0 ? (
              <Typography sx={{ py: 2, color: "#666" }}>No archived board members yet.</Typography>
            ) : (
              <>
                <MembersGrid>
                  {archivedPageSlice.map((member) => (
                    <MemberCard key={member._id}>
                      <ImageWithFallback
                        path={member.image}
                        alt={member.name}
                        style={{ objectFit: "contain", backgroundColor: "white" }}
                      />
                      <Typography variant="h3">{member.name}</Typography>
                      <Typography variant="body1">{member.title}</Typography>
                      <Typography variant="body2" color="#666" sx={{ mt: 0.5 }}>
                        {formatBoardDate(getStartDate(member))} – {formatBoardDate(member.endDate)}
                      </Typography>
                      <Button
                        component={Link}
                        to={`/board-members/${member._id}`}
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        sx={{ mt: 2 }}
                      >
                        View
                      </Button>
                    </MemberCard>
                  ))}
                </MembersGrid>
                {archivePageCount > 1 && (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 3, mb: 2 }}>
                    <Pagination
                      count={archivePageCount}
                      page={archivePage}
                      onChange={(_, page) => setArchivePage(page)}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

export default BoardMembersList;
